import { useState, type FormEvent } from "react";
import { ArrowLeft, CheckCircle2, Loader2, Search } from "lucide-react";
import { ApiError } from "@/lib/api";
import { checkTicketStatus, confirmTicketResolved, type PublicStatus } from "./api";

const TONES: Record<string, string> = {
  open: "bg-muted text-muted-foreground",
  assigned: "bg-primary/15 text-primary",
  in_progress: "bg-primary/15 text-primary",
  waiting_customer: "bg-sla-warn/15 text-sla-warn",
  resolved: "bg-sla-ok/15 text-sla-ok",
  closed: "bg-sla-ok/15 text-sla-ok",
  cancelled: "bg-destructive/15 text-destructive",
};

/**
 * Check a ticket's status without signing in — by ticket number + the email it
 * was raised with. No account, no internal detail: status and a plain timeline.
 */
export function PublicStatusForm({
  onBack,
  initialTicketNumber = "",
}: {
  onBack: () => void;
  initialTicketNumber?: string;
}) {
  const [ticketNumber, setTicketNumber] = useState(initialTicketNumber);
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<PublicStatus | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [confirmed, setConfirmed] = useState<string | null>(null);

  async function confirm() {
    if (!result) return;
    setError(null);
    setConfirming(true);
    try {
      const res = await confirmTicketResolved(result.ticket_number, email.trim());
      setConfirmed(res.message);
      setResult({ ...result, status: "closed", status_label: "Closed", can_confirm: false });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not confirm. Please try again.");
    } finally {
      setConfirming(false);
    }
  }

  const input =
    "h-10 w-full rounded-md border border-input bg-background px-3 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring";

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setResult(null);
    setLoading(true);
    try {
      setResult(await checkTicketStatus(ticketNumber.trim(), email.trim()));
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message
          : "Could not check the status. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="space-y-4 rounded-lg border border-border bg-card p-6">
      <p className="text-sm text-muted-foreground">
        Enter your ticket number and the email you used to raise it.
      </p>

      <form onSubmit={submit} className="space-y-3" noValidate>
        <div className="space-y-1.5">
          <label htmlFor="s_number" className="text-sm font-medium">Ticket number</label>
          <input
            id="s_number"
            className={input}
            placeholder="3HS-2026-000001"
            value={ticketNumber}
            onChange={(e) => setTicketNumber(e.target.value)}
            required
          />
        </div>
        <div className="space-y-1.5">
          <label htmlFor="s_email" className="text-sm font-medium">Email</label>
          <input
            id="s_email"
            type="email"
            autoComplete="email"
            className={input}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
        </div>

        {error && (
          <div role="alert" className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={loading || !ticketNumber.trim() || !email.trim()}
          className="inline-flex h-10 w-full items-center justify-center gap-2 rounded-md bg-primary text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : <Search className="h-4 w-4" aria-hidden="true" />}
          {loading ? "Checking…" : "Check status"}
        </button>
      </form>

      {result && (
        <div className="space-y-3 rounded-md border border-border p-4">
          <div className="flex items-center justify-between gap-2">
            <span className="font-mono text-sm">{result.ticket_number}</span>
            <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${TONES[result.status] ?? "bg-muted text-muted-foreground"}`}>
              {result.status_label}
            </span>
          </div>
          <p className="text-sm">{result.subject}</p>

          {result.engineer_name && (
            <p className="text-xs text-muted-foreground">
              Handled by <span className="font-medium text-foreground">{result.engineer_name}</span>
            </p>
          )}
          {result.resolution && (
            <div className="rounded-md bg-muted p-3 text-sm">
              <p className="mb-1 text-xs font-medium text-muted-foreground">Resolution</p>
              {result.resolution}
            </div>
          )}

          {result.timeline.length > 0 && (
            <ol className="space-y-1 border-t border-border pt-2">
              {result.timeline.map((e, i) => (
                <li key={i} className="flex items-center justify-between text-xs text-muted-foreground">
                  <span>{e.label}</span>
                  <span>{new Date(e.at).toLocaleString()}</span>
                </li>
              ))}
            </ol>
          )}

          {confirmed ? (
            <p className="inline-flex items-center gap-1.5 border-t border-border pt-3 text-sm text-sla-ok">
              <CheckCircle2 className="h-4 w-4" aria-hidden="true" />
              {confirmed}
            </p>
          ) : (
            result.can_confirm && (
              <div className="border-t border-border pt-3">
                <p className="mb-2 text-sm">Is your problem resolved?</p>
                <button
                  type="button"
                  onClick={() => void confirm()}
                  disabled={confirming}
                  className="inline-flex h-10 w-full items-center justify-center gap-2 rounded-md bg-sla-ok text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
                >
                  {confirming ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : <CheckCircle2 className="h-4 w-4" aria-hidden="true" />}
                  Yes, confirm resolved & close
                </button>
              </div>
            )
          )}
        </div>
      )}

      <button type="button" onClick={onBack} className="inline-flex w-full items-center justify-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="h-4 w-4" aria-hidden="true" />
        Back to sign in
      </button>
    </div>
  );
}
